import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { tap, finalize, delay, map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoaderService } from '../loader-service/loader.service';
import { DataRecord } from '../../models/data-record.model';
import { RecordApiResponse } from '../../models/type-records.model';
import { ColumnRequestBody } from '../../models/column-request-body.model';
import { SaveColumns } from '../../models/saveColumns.model';
import { DynamicApiResponse } from '../../models/type-dynamic-filter.model';
import { LastRecord } from '../../models/last-record.model';
import { GetAllFilters } from '../../models/get-all-filters.model';
import { AuthServiceService } from '../auth-service/auth-service.service';



@Injectable({
  providedIn: 'root',
})
export class ControlCenterService {

  
  public clearColumnsSubject = new Subject<void>();

  clearColumns$ = this.clearColumnsSubject.asObservable();

  public keysData: { allKeys: string[], selectedKeys: string[] } = { allKeys: [], selectedKeys: [] };
  private baseUrl = environment.baseUrl;
  private rtsBaseURL = environment.rtsBaseURL
  conditionRequestBody: any

  constructor(private http: HttpClient, private loaderService: LoaderService, private authService: AuthServiceService) { }

  // Unit test status: completed
  getRecordsByOrderID(rowId: string): Observable<any> {
    this.loaderService.show();
    const requestBody = {
      id: rowId
    };
    return this.http.post<any>(this.baseUrl + 'v1/desadv', requestBody).pipe(
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }

  // Unit test status: completed
  getOutboundRecords(pageIndex: number, getSelectedFields: any, pageSize: number, type: any): Observable<RecordApiResponse> {
    this.loaderService.show();
    const requestBody: any = {
      requestType: "records",
      page: pageIndex,
      numberPerPage: pageSize,
      selectedFields: getSelectedFields,
      sorts: {
        processTimeStamp: "desc"
      },
      type: type
    };

    const isUserExternal = localStorage.getItem('userType');
    
    if( isUserExternal == 'external'){
      requestBody.email = [this.authService.externalEmail];
    }
    const finalRequestBody = requestBody;

    return this.http.post<RecordApiResponse>(this.baseUrl + 'v1/epcis/records/merge', finalRequestBody).pipe(
      map((records: RecordApiResponse) => {
        this.separateKeys(records.data);
        return records;
      }),
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }



  // Unit test status : completed.
  getSortedRecordsForColumn(requestBody1: ColumnRequestBody): Observable<RecordApiResponse> {
    this.loaderService.show();
    return this.http.post<RecordApiResponse>(this.baseUrl + 'v1/epcis/records/merge', requestBody1).pipe(
      map(records => {
        return records;
      }),
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }

  // Unit test status: completed
  separateKeys(recordArray: any[]): { allKeys: string[], selectedKeys: string[] } {
    const allKeys: string[] = [];
    const selectedKeys: string[] = [];
    const keysToSelect = ['filename', 'gln', 'senderCompany', 'from', 'to', 'status', 'orderid', 'shipmentid', 'po', 'sgln', 'error'];
    // Ensure recordArray is an array
    if (!Array.isArray(recordArray)) {

      return { allKeys: [], selectedKeys: [] };
    }
    recordArray.forEach((record: {}) => {
      if (record && typeof record === 'object') {
        Object.keys(record).forEach(key => {
          if (key !== 'id' && key !== 'errors' && key !== 'warnings' && key != "error_message" && key != "warning_message" && key != "error_code" && key != "warning_code" && key != "error_details" && key != "warning_details" && key != "warningCode" && key != "warningDetails" && key != "filePath" && key != "serialVersionUID" && key != 'type') {
            allKeys.push(key);
            if (keysToSelect.includes(key)) {
              selectedKeys.push(key);
            }
          }
        });
      }
    });

    const staticKeys = [
      "processTimeStamp",
      "filename",
      "status",
      "error",
      "warning",
      "po",
      "desadv",
      "inv",
      "processingTime"
    ];

    this.keysData.allKeys = [...new Set([...allKeys, ...staticKeys])];
    this.keysData.selectedKeys = [...new Set(selectedKeys)];
    return this.keysData;
  }
  // Unit test status: completed
  getKeysData(): { allKeys: string[], selectedKeys: string[] } {
    return this.keysData;
  }

  // Unit test status: completed
  updateSelectedKeys(key: string, isChecked: boolean) {
    if (isChecked) {
      if (!this.keysData.selectedKeys.includes(key)) {
        this.keysData.selectedKeys.push(key);
      }
    } else {
      const index = this.keysData.selectedKeys.indexOf(key);
      if (index > -1) {
        this.keysData.selectedKeys.splice(index, 1);
      }
    }
  }

  // Unit test status: completed
  sendColumnNames(fields: any): Observable<SaveColumns> {
    return this.http.post<SaveColumns>(this.baseUrl + 'v1/saveColumns', fields).pipe(
      finalize(() => {
        this.loaderService.hide(); // No need for extra observable here
      })
    );
  }

  // Unit test status: done
  filterApply(requestBody: any): Observable<DynamicApiResponse> {
    this.loaderService.show();
    return this.http.post<DynamicApiResponse>(this.baseUrl + 'v1/epcis/records/merge', requestBody).pipe(
      map(records => {
        this.separateKeys(records.data);
        return records;
      }),
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }
 
  bulkExport(requestBody: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'v1/export/export-bulk-report', requestBody, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob' as 'json',
      observe: 'response'
    })
  }
  // Unit test status: done
  saveFilter(filter: any): Observable<string> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<string>(this.baseUrl + 'v1/saveConditions', filter, { headers });
  }

  // Unit test status: done
  getFilters(): Observable<Array<GetAllFilters>> {
    return this.http.get<Array<GetAllFilters>>(this.baseUrl + 'v1/getAll');
  }

  // Unit test status: done
  getfetchSelectedColumList(): Observable<LastRecord> {
    this.loaderService.show();
    return this.http.get<LastRecord>(this.baseUrl + 'v1/getLastRecord')
  }

  // Unit test status: done
  deleteFilter(id: number): Observable<void> {
    return this.http.delete<any>(`${this.baseUrl}v1/filters/${id}`,{responseType:"text" as "json"});
  }
  deleteLayout(id: number): Observable<void> {
    return this.http.delete<any>(`${this.baseUrl}v1/SelectedFields/${id}`,{responseType:"text" as "json"});
  }

  // need to refactor in future right now we are going with the existing code setup
  // Unit test status: done
  columnFilterDebounceAction(requestBody: any) {
    return this.http.post<any>(this.baseUrl + 'v1/epcis/records/merge/unique', requestBody).pipe(
      map((records: { status: number | string; total: number; uniqueData: Array<{ [key: string]: Array<any> }> }) => {
        return records;
      }),
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }

  fetchColumnNamesForDynamicFilter() {
    this.loaderService.show();
    return this.http.post<any>(this.baseUrl + 'v1/epcis/records/merge/epcisList', null).pipe(
      map((records: any) => {
        return records;
      }),
      finalize(() => {
        of(null).pipe(delay(1000)).subscribe(() => {
          this.loaderService.hide();
        });
      })
    );
  }

  fetchXmlFileToDisplayOnCunsumEpcisPage(rowId: string): Observable<any> {
    const url = environment.baseUrl + 'v1/epcis/downloadFile';
    const requestBody = { id: rowId };

    return this.http.post<any>(url, requestBody).pipe(
      tap(() => this.loaderService.show()),
      map((response: any) => {
        if (!response || !response.data) {
          throw new Error('No file found');
        }
        return response.data;
      }),
      catchError((error) => {
        if (error.status === 500) {
          return of({ message: 'File not found or an internal server error occurred.' });
        }

        return throwError(() => new Error(error.message || 'Something went wrong'));
      }),
      finalize(() => {
        setTimeout(() => this.loaderService.hide(), 1000);
      })
    );
  }


  downloadErrorAndWarningFromActionColumn(rowId: any): Observable<any> {
    const baseUrl = environment.baseUrl + 'v1/epcis/download-zip';
    const requestBody = { id: rowId };
    return this.http.post(baseUrl, requestBody, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob',
      observe: 'response'
    });
  }

  downloadAllControlCenterData(requestBodyForExport: any): Observable<any> {
    const urlForExportData = `${environment.baseUrl}v1/export/export-report`;
    const requestBody = requestBodyForExport;
    return this.http.post<any>(urlForExportData, requestBody, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob' as 'json',
      observe: 'response'
    })
  }

  // getAllLayoutRecords(): Observable<any[]> {
  //   return this.http.get<any[]>`${environment.baseUrl}getAllSelectedField`;
  // }
  getAllLayoutRecords(): Observable<any[]> {
    return this.http.get<any[]>(environment.baseUrl + 'v1/getAllSelectedField');
  }

  triggerClearColumns() {
    this.clearColumnsSubject.next();
  }

  reGenerateOutbound(eventList: any): Observable<string> {

    return this.http.post<string>(this.rtsBaseURL + '/v1/events/reprocess', eventList,{headers : new HttpHeaders({
      'Authorization': 'Basic ' + btoa(`${environment.rtsUserName}`),
      'Accept': 'application/json'
    })});
  }

}